import { createContext, useContext } from 'react';
import {
  Node,
  mergeAttributes,
  NodeViewContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type NodeViewProps,
} from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Fragment, Slice, type Node as PMNode } from '@tiptap/pm/model';
import { FileText } from 'lucide-react';

/**
 * Slice 19c — a form note inserted into the narrative, awaiting the author's
 * Keep or Remove.
 *
 * Stored as `{ type: 'formNoteInsertion', attrs: { insertionId }, content:
 * [blocks] }` (ADR-0002). The server only accepts it at the top level of a
 * section, and reconciles it against the insertion ledger on every save, so
 * the editor never has to be the source of truth.
 *
 * Buttons come from FormNoteInsertionContext, which the section editor
 * provides. Without it (the read-only preview) the block renders with its
 * label only.
 */
export type FormNoteInsertionActions = {
  /** "3.2.1" style label for the block header, when the drawer knows it. */
  labelFor: (insertionId: string) => string | undefined;
  keep: (insertionId: string) => void;
  remove: (insertionId: string) => void;
  busy: boolean;
};

export const FormNoteInsertionContext = createContext<FormNoteInsertionActions | null>(null);

export const FORM_NOTE_INSERTION = 'formNoteInsertion';

export const FormNoteInsertion = Node.create({
  name: FORM_NOTE_INSERTION,
  group: 'block',
  // Ordinary narrative blocks only: never another insertion.
  content: '(paragraph | heading | bulletList | orderedList)+',
  defining: true,
  isolating: true,

  addAttributes() {
    return {
      insertionId: {
        default: null,
        parseHTML: el => el.getAttribute('data-insertion-id'),
        renderHTML: attrs => ({ 'data-insertion-id': attrs.insertionId as string }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-form-note-insertion]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-form-note-insertion': '' }), 0];
  },

  addNodeView() {
    return ReactNodeViewRenderer(FormNoteInsertionView);
  },

  addProseMirrorPlugins() {
    return [unwrapOnPaste];
  },
});

/**
 * A pasted insertion is just text: drop the marker, keep the content. A copy
 * of a pending block must not become a second pending block. The server's
 * reconciler does the same, so this is about what the author sees.
 */
const unwrapOnPaste = new Plugin({
  key: new PluginKey('formNoteInsertionUnwrapOnPaste'),
  props: {
    transformPasted(slice) {
      const { fragment, changed } = unwrap(slice.content);
      return changed ? new Slice(fragment, 0, 0) : slice;
    },
  },
});

function unwrap(fragment: Fragment): { fragment: Fragment; changed: boolean } {
  let changed = false;
  const nodes: PMNode[] = [];
  fragment.forEach(node => {
    if (node.type.name === FORM_NOTE_INSERTION) {
      changed = true;
      node.content.forEach(child => nodes.push(child));
      return;
    }
    const inner = unwrap(node.content);
    changed ||= inner.changed;
    nodes.push(inner.changed ? node.copy(inner.fragment) : node);
  });
  return { fragment: changed ? Fragment.fromArray(nodes) : fragment, changed };
}

function FormNoteInsertionView({ node, editor }: NodeViewProps) {
  const actions = useContext(FormNoteInsertionContext);
  const insertionId = node.attrs.insertionId as string;
  const label = actions?.labelFor(insertionId);
  const canAct = !!actions && editor.isEditable;

  return (
    <NodeViewWrapper
      data-insertion-id={insertionId}
      className="my-4 rounded-md border border-dashed border-amber-300 bg-amber-50/70 not-prose">
      <div contentEditable={false}
           className="flex items-center gap-2 px-3 py-1.5 border-b border-amber-200 text-xs select-none">
        <FileText className="w-3.5 h-3.5 text-amber-700 shrink-0" aria-hidden />
        <span className="font-semibold text-amber-900">
          {label ? `From note ${label}` : 'From a form note'}
        </span>
        <span className="text-amber-800">· awaiting your review</span>
        {canAct && (
          <span className="ml-auto flex items-center gap-2">
            <button type="button"
                    disabled={actions.busy}
                    onClick={() => actions.remove(insertionId)}
                    className="px-2 py-1 rounded text-amber-900 hover:bg-amber-100
                               disabled:opacity-40">
              Remove
            </button>
            <button type="button"
                    disabled={actions.busy}
                    onClick={() => actions.keep(insertionId)}
                    className="px-2.5 py-1 rounded border border-amber-300 bg-white
                               font-medium text-gray-900 hover:bg-amber-50 disabled:opacity-40">
              Keep
            </button>
          </span>
        )}
      </div>
      <NodeViewContent className="px-3 py-1 prose prose-sm max-w-none" />
    </NodeViewWrapper>
  );
}
